// src/features/urlaubsplaner/components/UrlaubsplanerDashboard.tsx

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Plus, Loader, ChevronDown, ChevronUp } from 'lucide-react';
import { useUrlaub } from '../hooks/useUrlaub';
import { UrlaubFormModal } from './UrlaubFormModal';
import { StaffUrlaubMitName, UrlaubFormData, ABWESENHEITS_ARTEN, ABWESENHEITS_FARBEN } from '../types';
import { staffService } from '../../../services/staff/StaffService';
import { urlaubService } from '../services/urlaubService';
import { deadlineCalculationService, DeadlineHeatmapData } from '../../../shared/services/deadlineCalculationService';
import { isHolidayOrWeekendNRW } from '../../../shared/utils/dateUtils';

const WOCHENTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONATSNAMEN = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

interface UrlaubsplanerDashboardProps {
  user: any;
}

// Hilfstyp für Jahresübersicht
interface StaffJahresuebersicht {
  staff_id: string;
  staff_name: string;
  staff_initials: string;
  urlaubstage: number;
  sonstigeTage: number;
  eintraege: StaffUrlaubMitName[];
}

/**
 * Bestimmt die Heatmap-Hintergrundfarbe basierend auf der Deadline-Anzahl.
 * Dezente Rot-Abstufungen, die mit den bestehenden Farben harmonieren.
 */
const getHeatmapColor = (anzahl: number): string | null => {
  if (anzahl === 0) return null;
  if (anzahl <= 2) return '#FEF2F2';   // Sehr dezent
  if (anzahl <= 5) return '#FEE2E2';   // Leicht
  if (anzahl <= 10) return '#FECACA';  // Mittel
  if (anzahl <= 20) return '#FCA5A5';  // Deutlich
  return '#F87171';                     // Stark (>20 Deadlines)
};

/**
 * Bestimmt die Text-Farbe der Deadline-Anzahl basierend auf der Intensität.
 */
const getHeatmapTextColor = (anzahl: number): string => {
  if (anzahl <= 5) return '#DC2626';
  if (anzahl <= 10) return '#B91C1C';
  return '#991B1B';
};

/**
 * Zählt die Arbeitstage (ohne Wochenenden und NRW-Feiertage) in einem Zeitraum.
 * Bei ist_halber_tag = true wird immer 0.5 zurückgegeben.
 */
const berechneArbeitstage = (von: string, bis: string, istHalberTag: boolean): number => {
  if (istHalberTag) return 0.5;

  const start = new Date(von);
  const ende = new Date(bis);
  let arbeitstage = 0;

  const current = new Date(start);
  while (current <= ende) {
    if (!isHolidayOrWeekendNRW(current)) {
      arbeitstage++;
    }
    current.setDate(current.getDate() + 1);
  }

  return arbeitstage;
};

/**
 * Zählt die Arbeitstage (ohne Wochenenden und NRW-Feiertage), geclippt auf Jahresgrenzen.
 * Bei ist_halber_tag = true wird 0.5 zurückgegeben (wenn der Tag im Jahr liegt).
 */
const berechneArbeitstageImJahr = (von: string, bis: string, zielJahr: number, istHalberTag: boolean): number => {
  const jahresAnfang = new Date(zielJahr, 0, 1);
  const jahresEnde = new Date(zielJahr, 11, 31);
  const start = new Date(von);
  const ende = new Date(bis);

  const clippedStart = start < jahresAnfang ? jahresAnfang : start;
  const clippedEnde = ende > jahresEnde ? jahresEnde : ende;

  if (clippedStart > clippedEnde) return 0;

  if (istHalberTag) return 0.5;

  let arbeitstage = 0;
  const current = new Date(clippedStart);
  while (current <= clippedEnde) {
    if (!isHolidayOrWeekendNRW(current)) {
      arbeitstage++;
    }
    current.setDate(current.getDate() + 1);
  }

  return arbeitstage;
};

/**
 * Formatiert eine Tageanzahl für die Anzeige (z.B. 0.5 → "0,5", 3 → "3")
 */
const formatTage = (tage: number): string => {
  if (tage % 1 !== 0) {
    return tage.toFixed(1).replace('.', ',');
  }
  return String(tage);
};

export const UrlaubsplanerDashboard: React.FC<UrlaubsplanerDashboardProps> = ({ user }) => {
  const heute = new Date();
  const [monat, setMonat] = useState(heute.getMonth() + 1); // 1-12
  const [jahr, setJahr] = useState(heute.getFullYear());
  const [showFormModal, setShowFormModal] = useState(false);
  const [bearbeitungsEintrag, setBearbeitungsEintrag] = useState<StaffUrlaubMitName | null>(null);
  const [currentStaffId, setCurrentStaffId] = useState<string | null>(null);

  const isAdmin = user?.email === 'tim.wolter@xtax.de';

  // Jahresübersicht State (nur Admin)
  const [jahresAbwesenheiten, setJahresAbwesenheiten] = useState<StaffUrlaubMitName[]>([]);
  const [jahresLoading, setJahresLoading] = useState(false);
  const [aufgeklappteStaff, setAufgeklappteStaff] = useState<Set<string>>(new Set());

  // Deadline-Heatmap State
  const [deadlineHeatmap, setDeadlineHeatmap] = useState<DeadlineHeatmapData>({});
  const [heatmapLoading, setHeatmapLoading] = useState(false);

  // Eigene Staff-ID ermitteln
  useEffect(() => {
    const loadCurrentStaffId = async () => {
      if (!user?.email) return;
      try {
        const allStaff = await staffService.getActiveStaff();
        const currentStaff = allStaff.find(
          staff => staff.email.toLowerCase() === user.email.toLowerCase()
        );
        if (currentStaff) {
          setCurrentStaffId(currentStaff.id);
        }
      } catch (error) {
        console.error('Fehler beim Laden der eigenen Staff-ID:', error);
      }
    };
    loadCurrentStaffId();
  }, [user?.email]);

  const {
    abwesenheiten,
    loading,
    erstelleAbwesenheit,
    aktualisiereAbwesenheit,
    loescheAbwesenheit
  } = useUrlaub(monat, jahr);

  // Jahresübersicht laden: für Admin alle Mitarbeiter, für Mitarbeiter clientseitig gefiltert.
  // Reagiert auch auf Änderungen der Monats-Abwesenheiten.
  const ladeJahresuebersicht = useCallback(async () => {
    if (!isAdmin && !currentStaffId) return;
    setJahresLoading(true);
    try {
      const daten = await urlaubService.getAbwesenheitenFuerJahr(jahr);
      setJahresAbwesenheiten(daten);
    } catch (error) {
      console.error('Fehler beim Laden der Jahresübersicht:', error);
    } finally {
      setJahresLoading(false);
    }
  }, [isAdmin, jahr, currentStaffId]);

  useEffect(() => {
    ladeJahresuebersicht();
  }, [ladeJahresuebersicht, abwesenheiten]);

  // Deadline-Heatmap laden
  // Admin sieht alle Deadlines (staffId = null), Mitarbeiter nur eigene
  const ladeHeatmap = useCallback(async () => {
    // Warte bis currentStaffId geladen ist (außer für Admin)
    if (!isAdmin && !currentStaffId) return;

    setHeatmapLoading(true);
    try {
      const staffIdFuerHeatmap = isAdmin ? null : currentStaffId;
      const daten = await deadlineCalculationService.getDeadlineHeatmapForMonth(
        monat,
        jahr,
        staffIdFuerHeatmap
      );
      setDeadlineHeatmap(daten);
    } catch (error) {
      console.error('Fehler beim Laden der Deadline-Heatmap:', error);
      setDeadlineHeatmap({});
    } finally {
      setHeatmapLoading(false);
    }
  }, [monat, jahr, isAdmin, currentStaffId]);

  useEffect(() => {
    ladeHeatmap();
  }, [ladeHeatmap]);

  // Navigation
  const vorherigerMonat = () => {
    if (monat === 1) {
      setMonat(12);
      setJahr(jahr - 1);
    } else {
      setMonat(monat - 1);
    }
  };

  const naechsterMonat = () => {
    if (monat === 12) {
      setMonat(1);
      setJahr(jahr + 1);
    } else {
      setMonat(monat + 1);
    }
  };

  const heuteNavigieren = () => {
    setMonat(heute.getMonth() + 1);
    setJahr(heute.getFullYear());
  };

  // Kalender-Tage berechnen
  const kalenderTage = useMemo(() => {
    const ersterTag = new Date(jahr, monat - 1, 1);
    const letzterTag = new Date(jahr, monat, 0);
    const tageImMonat = letzterTag.getDate();

    // Wochentag des ersten Tages (0 = Sonntag, wir wollen 0 = Montag)
    let startWochentag = ersterTag.getDay() - 1;
    if (startWochentag < 0) startWochentag = 6;

    const tage: Array<{ tag: number; imMonat: boolean; datum: string }> = [];

    // Leere Tage vor dem Monatsbeginn
    for (let i = 0; i < startWochentag; i++) {
      tage.push({ tag: 0, imMonat: false, datum: '' });
    }

    // Tage im Monat
    for (let tag = 1; tag <= tageImMonat; tag++) {
      const datum = `${jahr}-${String(monat).padStart(2, '0')}-${String(tag).padStart(2, '0')}`;
      tage.push({ tag, imMonat: true, datum });
    }

    return tage;
  }, [monat, jahr]);

  // Abwesenheiten für einen bestimmten Tag finden
  const getAbwesenheitenFuerTag = (datum: string): StaffUrlaubMitName[] => {
    if (!datum) return [];
    return abwesenheiten.filter(a => a.von <= datum && a.bis >= datum);
  };

  // Ist heute?
  const istHeute = (datum: string): boolean => {
    const heuteStr = `${heute.getFullYear()}-${String(heute.getMonth() + 1).padStart(2, '0')}-${String(heute.getDate()).padStart(2, '0')}`;
    return datum === heuteStr;
  };

  // Wochenende?
  const istWochenende = (datum: string): boolean => {
    if (!datum) return false;
    const d = new Date(datum);
    return d.getDay() === 0 || d.getDay() === 6;
  };

  // Berechtigungsprüfung: Darf der User diesen Eintrag bearbeiten?
  const darfBearbeiten = (eintrag: StaffUrlaubMitName): boolean => {
    if (isAdmin) return true;
    return eintrag.staff_id === currentStaffId;
  };

  // Form Handlers
  const handleNeuClick = () => {
    setBearbeitungsEintrag(null);
    setShowFormModal(true);
  };

  const handleEintragClick = (eintrag: StaffUrlaubMitName) => {
    if (!darfBearbeiten(eintrag)) return;
    setBearbeitungsEintrag(eintrag);
    setShowFormModal(true);
  };

  const handleSave = async (formData: UrlaubFormData) => {
    if (bearbeitungsEintrag) {
      return aktualisiereAbwesenheit(bearbeitungsEintrag.id, formData);
    }
    return erstelleAbwesenheit(formData);
  };

  const handleDelete = async (id: string) => {
    return loescheAbwesenheit(id);
  };

  // Deutsches Datum formatieren
  const formatieresDatum = (datum: string): string => {
    const d = new Date(datum);
    return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
  };

  // Jahresübersicht: Gruppiert nach Mitarbeiter
  const jahresuebersicht = useMemo((): StaffJahresuebersicht[] => {
    if (!isAdmin || jahresAbwesenheiten.length === 0) return [];

    const staffMap = new Map<string, StaffJahresuebersicht>();

    for (const eintrag of jahresAbwesenheiten) {
      if (!staffMap.has(eintrag.staff_id)) {
        staffMap.set(eintrag.staff_id, {
          staff_id: eintrag.staff_id,
          staff_name: eintrag.staff_name,
          staff_initials: eintrag.staff_initials,
          urlaubstage: 0,
          sonstigeTage: 0,
          eintraege: []
        });
      }

      const staff = staffMap.get(eintrag.staff_id)!;
      const tage = berechneArbeitstageImJahr(eintrag.von, eintrag.bis, jahr, eintrag.ist_halber_tag || false);

      if (eintrag.art === 'urlaub') {
        staff.urlaubstage += tage;
      } else {
        staff.sonstigeTage += tage;
      }

      staff.eintraege.push(eintrag);
    }

    // Sortiert nach Name
    return Array.from(staffMap.values()).sort((a, b) => a.staff_name.localeCompare(b.staff_name));
  }, [isAdmin, jahresAbwesenheiten, jahr]);

  // Eigene Urlaubstage im Jahr (nur für Mitarbeiter, gefiltert auf currentStaffId, nur Art "urlaub")
  const eigeneUrlaubstageImJahr = useMemo((): number => {
    if (isAdmin || !currentStaffId || jahresAbwesenheiten.length === 0) return 0;

    let summe = 0;
    for (const eintrag of jahresAbwesenheiten) {
      if (eintrag.staff_id !== currentStaffId) continue;
      if (eintrag.art !== 'urlaub') continue;
      summe += berechneArbeitstageImJahr(eintrag.von, eintrag.bis, jahr, eintrag.ist_halber_tag || false);
    }
    return summe;
  }, [isAdmin, currentStaffId, jahresAbwesenheiten, jahr]);

  // Toggle aufklappen
  const toggleStaffDetails = (staffId: string) => {
    setAufgeklappteStaff(prev => {
      const next = new Set(prev);
      if (next.has(staffId)) {
        next.delete(staffId);
      } else {
        next.add(staffId);
      }
      return next;
    });
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px 0' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', margin: 0 }}>
          Urlaubsplaner
        </h1>
        <button
          onClick={handleNeuClick}
          style={{
            padding: '10px 20px',
            backgroundColor: '#3B82F6',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Plus size={18} />
          Neue Abwesenheit
        </button>
      </div>

      {/* Monats-Navigation */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        backgroundColor: '#F9FAFB',
        padding: '12px 16px',
        borderRadius: '10px',
        border: '1px solid #E5E7EB'
      }}>
        <button
          onClick={vorherigerMonat}
          style={{
            background: 'none',
            border: '1px solid #D1D5DB',
            borderRadius: '6px',
            cursor: 'pointer',
            padding: '6px 10px',
            color: '#374151',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <ChevronLeft size={18} />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>
            {MONATSNAMEN[monat - 1]} {jahr}
          </span>
          <button
            onClick={heuteNavigieren}
            style={{
              padding: '4px 12px',
              backgroundColor: '#FFFFFF',
              color: '#374151',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '12px',
              cursor: 'pointer'
            }}
          >
            Heute
          </button>
        </div>

        <button
          onClick={naechsterMonat}
          style={{
            background: 'none',
            border: '1px solid #D1D5DB',
            borderRadius: '6px',
            cursor: 'pointer',
            padding: '6px 10px',
            color: '#374151',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Legende */}
      <div style={{
        display: 'flex',
        gap: '20px',
        marginBottom: '16px',
        fontSize: '13px',
        color: '#6B7280',
        flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{
            width: '12px',
            height: '12px',
            borderRadius: '3px',
            backgroundColor: ABWESENHEITS_FARBEN.urlaub
          }} />
          <span>Urlaub</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{
            width: '12px',
            height: '12px',
            borderRadius: '3px',
            backgroundColor: ABWESENHEITS_FARBEN.sonstige_abwesenheit
          }} />
          <span>Sonstige Abwesenheit</span>
        </div>
        {/* Heatmap Legende */}
        {!heatmapLoading && Object.keys(deadlineHeatmap).length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{
              display: 'flex',
              gap: '2px',
              alignItems: 'center'
            }}>
              <div style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: '#FEF2F2', border: '1px solid #FECACA' }} />
              <div style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: '#FEE2E2' }} />
              <div style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: '#FECACA' }} />
              <div style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: '#FCA5A5' }} />
            </div>
            <span>{isAdmin ? 'Fristen (alle Kunden)' : 'Meine Fristen'}</span>
          </div>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '40px',
          color: '#6B7280'
        }}>
          <Loader size={20} style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }} />
          Laden...
        </div>
      )}

      {/* Kalender */}
      {!loading && (
        <div style={{
          border: '1px solid #E5E7EB',
          borderRadius: '10px',
          overflow: 'hidden',
          backgroundColor: '#FFFFFF'
        }}>
          {/* Wochentage Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            backgroundColor: '#F9FAFB',
            borderBottom: '1px solid #E5E7EB'
          }}>
            {WOCHENTAGE.map(tag => (
              <div key={tag} style={{
                padding: '10px 8px',
                textAlign: 'center',
                fontSize: '12px',
                fontWeight: '600',
                color: '#6B7280',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                {tag}
              </div>
            ))}
          </div>

          {/* Kalender-Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)'
          }}>
            {kalenderTage.map((tag, index) => {
              const tagAbwesenheiten = tag.imMonat ? getAbwesenheitenFuerTag(tag.datum) : [];
              const heuteHighlight = tag.imMonat && istHeute(tag.datum);
              const wochenende = tag.imMonat && istWochenende(tag.datum);

              // Heatmap: Deadline-Anzahl für diesen Tag
              const deadlineAnzahl = tag.imMonat ? (deadlineHeatmap[tag.datum] || 0) : 0;
              const heatmapBg = tag.imMonat && !wochenende ? getHeatmapColor(deadlineAnzahl) : null;

              // Tooltip für Deadlines
              const deadlineTooltip = deadlineAnzahl > 0
                ? `${deadlineAnzahl} ${deadlineAnzahl === 1 ? 'Frist' : 'Fristen'} an diesem Tag`
                : '';

              return (
                <div
                  key={index}
                  title={deadlineTooltip}
                  style={{
                    minHeight: '90px',
                    padding: '4px',
                    borderRight: (index + 1) % 7 !== 0 ? '1px solid #F3F4F6' : 'none',
                    borderBottom: '1px solid #F3F4F6',
                    backgroundColor: !tag.imMonat
                      ? '#FAFAFA'
                      : heatmapBg
                        ? heatmapBg
                        : wochenende
                          ? '#F9FAFB'
                          : '#FFFFFF'
                  }}
                >
                  {tag.imMonat && (
                    <>
                      {/* Tag-Nummer + Deadline-Badge */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '4px',
                        paddingLeft: '2px',
                        paddingRight: '2px'
                      }}>
                        <div style={{
                          fontSize: '13px',
                          fontWeight: heuteHighlight ? '700' : '400',
                          color: heuteHighlight ? '#FFFFFF' : wochenende ? '#9CA3AF' : '#374151',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: heuteHighlight ? '24px' : 'auto',
                          height: heuteHighlight ? '24px' : 'auto',
                          borderRadius: '50%',
                          backgroundColor: heuteHighlight ? '#3B82F6' : 'transparent'
                        }}>
                          {tag.tag}
                        </div>

                        {/* Deadline-Anzahl Badge */}
                        {deadlineAnzahl > 0 && !wochenende && (
                          <div style={{
                            fontSize: '10px',
                            fontWeight: '600',
                            color: getHeatmapTextColor(deadlineAnzahl),
                            backgroundColor: 'rgba(255,255,255,0.7)',
                            padding: '1px 5px',
                            borderRadius: '8px',
                            lineHeight: '1.4'
                          }}>
                            {deadlineAnzahl}
                          </div>
                        )}
                      </div>

                      {/* Abwesenheiten */}
                      {tagAbwesenheiten.map(eintrag => {
                        const bearbeitbar = darfBearbeiten(eintrag);
                        return (
                          <div
                            key={eintrag.id}
                            onClick={() => handleEintragClick(eintrag)}
                            title={`${eintrag.staff_name} - ${ABWESENHEITS_ARTEN[eintrag.art]}${eintrag.ist_halber_tag ? ' (½ Tag)' : ''}${eintrag.notiz ? ': ' + eintrag.notiz : ''}`}
                            style={{
                              backgroundColor: ABWESENHEITS_FARBEN[eintrag.art],
                              color: '#FFFFFF',
                              fontSize: '11px',
                              fontWeight: '500',
                              padding: '2px 5px',
                              borderRadius: '4px',
                              marginBottom: '2px',
                              cursor: bearbeitbar ? 'pointer' : 'default',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              lineHeight: '1.4',
                              opacity: bearbeitbar ? 1 : 0.8
                            }}
                          >
                            {eintrag.staff_initials} {eintrag.staff_name.split(' ')[0]}{eintrag.ist_halber_tag ? ' ½' : ''}
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Abwesenheits-Liste unter dem Kalender */}
      {!loading && abwesenheiten.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>
            Abwesenheiten im {MONATSNAMEN[monat - 1]} {jahr}
          </h2>
          <div style={{
            border: '1px solid #E5E7EB',
            borderRadius: '10px',
            overflow: 'hidden',
            backgroundColor: '#FFFFFF'
          }}>
            {abwesenheiten.map((eintrag, index) => {
              const bearbeitbar = darfBearbeiten(eintrag);
              const tage = berechneArbeitstage(eintrag.von, eintrag.bis, eintrag.ist_halber_tag || false);
              return (
                <div
                  key={eintrag.id}
                  onClick={() => handleEintragClick(eintrag)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    borderBottom: index < abwesenheiten.length - 1 ? '1px solid #F3F4F6' : 'none',
                    cursor: bearbeitbar ? 'pointer' : 'default',
                    transition: 'background-color 0.15s'
                  }}
                  onMouseEnter={(e) => {
                    if (bearbeitbar) e.currentTarget.style.backgroundColor = '#F9FAFB';
                  }}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FFFFFF'}
                >
                  {/* Farb-Indikator */}
                  <div style={{
                    width: '4px',
                    height: '36px',
                    borderRadius: '2px',
                    backgroundColor: ABWESENHEITS_FARBEN[eintrag.art],
                    flexShrink: 0
                  }} />

                  {/* Initialen Badge */}
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    backgroundColor: '#EFF6FF',
                    color: '#3B82F6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '13px',
                    fontWeight: '600',
                    flexShrink: 0
                  }}>
                    {eintrag.staff_initials}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                      {eintrag.staff_name}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6B7280' }}>
                      {eintrag.ist_halber_tag
                        ? `${formatieresDatum(eintrag.von)} · ½ Tag`
                        : `${formatieresDatum(eintrag.von)} — ${formatieresDatum(eintrag.bis)} · ${formatTage(tage)} ${tage === 1 ? 'Arbeitstag' : 'Arbeitstage'}`
                      }
                      {eintrag.notiz && ` · ${eintrag.notiz}`}
                    </div>
                  </div>

                  {/* Art Badge */}
                  <div style={{
                    padding: '4px 10px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '500',
                    backgroundColor: eintrag.art === 'urlaub' ? '#EFF6FF' : '#FFFBEB',
                    color: eintrag.art === 'urlaub' ? '#2563EB' : '#D97706',
                    flexShrink: 0
                  }}>
                    {ABWESENHEITS_ARTEN[eintrag.art]}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Keine Abwesenheiten */}
      {!loading && abwesenheiten.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          color: '#9CA3AF',
          fontSize: '14px',
          marginTop: '16px'
        }}>
          Keine Abwesenheiten im {MONATSNAMEN[monat - 1]} {jahr} eingetragen.
        </div>
      )}

      {/* ============================================ */}
      {/* MITARBEITER: Eigene Jahressumme Urlaubstage  */}
      {/* ============================================ */}
      {!isAdmin && !jahresLoading && (
        <div style={{ marginTop: '32px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>
            Deine Urlaubstage {jahr}
          </h2>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 16px',
            border: '1px solid #E5E7EB',
            borderRadius: '10px',
            backgroundColor: '#FFFFFF'
          }}>
            <div style={{ fontSize: '14px', color: '#374151' }}>
              Geplante Urlaubstage in {jahr}
            </div>
            <div style={{
              padding: '4px 10px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '600',
              backgroundColor: '#EFF6FF',
              color: '#2563EB'
            }}>
              {formatTage(eigeneUrlaubstageImJahr)} {eigeneUrlaubstageImJahr === 1 ? 'Tag' : 'Tage'} Urlaub
            </div>
          </div>
        </div>
      )}

      {!isAdmin && jahresLoading && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '20px',
          color: '#6B7280',
          marginTop: '24px'
        }}>
          <Loader size={16} style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }} />
          Jahresübersicht laden...
        </div>
      )}

      {/* ============================================ */}
      {/* JAHRESÜBERSICHT - Nur für Admin sichtbar     */}
      {/* ============================================ */}
      {isAdmin && !jahresLoading && jahresuebersicht.length > 0 && (
        <div style={{ marginTop: '32px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>
            Jahresübersicht {jahr}
          </h2>
          <div style={{
            border: '1px solid #E5E7EB',
            borderRadius: '10px',
            overflow: 'hidden',
            backgroundColor: '#FFFFFF'
          }}>
            {jahresuebersicht.map((staff, index) => {
              const istAufgeklappt = aufgeklappteStaff.has(staff.staff_id);
              const gesamtTage = staff.urlaubstage + staff.sonstigeTage;

              return (
                <div key={staff.staff_id}>
                  {/* Mitarbeiter-Zeile */}
                  <div
                    onClick={() => toggleStaffDetails(staff.staff_id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '14px 16px',
                      borderBottom: (istAufgeklappt || index < jahresuebersicht.length - 1) ? '1px solid #F3F4F6' : 'none',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FFFFFF'}
                  >
                    {/* Initialen Badge */}
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      backgroundColor: '#EFF6FF',
                      color: '#3B82F6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '13px',
                      fontWeight: '600',
                      flexShrink: 0
                    }}>
                      {staff.staff_initials}
                    </div>

                    {/* Name */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                        {staff.staff_name}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6B7280' }}>
                        {staff.eintraege.length} {staff.eintraege.length === 1 ? 'Eintrag' : 'Einträge'}
                      </div>
                    </div>

                    {/* Tage-Badges */}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                      {staff.urlaubstage > 0 && (
                        <div style={{
                          padding: '4px 10px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor: '#EFF6FF',
                          color: '#2563EB'
                        }}>
                          {formatTage(staff.urlaubstage)} {staff.urlaubstage === 1 ? 'Tag' : 'Tage'} Urlaub
                        </div>
                      )}
                      {staff.sonstigeTage > 0 && (
                        <div style={{
                          padding: '4px 10px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor: '#FFFBEB',
                          color: '#D97706'
                        }}>
                          {formatTage(staff.sonstigeTage)} {staff.sonstigeTage === 1 ? 'Tag' : 'Tage'} Sonst.
                        </div>
                      )}
                      {staff.urlaubstage > 0 && staff.sonstigeTage > 0 && (
                        <div style={{
                          padding: '4px 10px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor: '#F3F4F6',
                          color: '#374151'
                        }}>
                          Σ {formatTage(gesamtTage)} {gesamtTage === 1 ? 'Tag' : 'Tage'}
                        </div>
                      )}
                    </div>

                    {/* Aufklapp-Icon */}
                    <div style={{ flexShrink: 0, color: '#9CA3AF' }}>
                      {istAufgeklappt ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </div>
                  </div>

                  {/* Aufgeklappte Detail-Liste */}
                  {istAufgeklappt && (
                    <div style={{
                      backgroundColor: '#FAFAFA',
                      borderBottom: index < jahresuebersicht.length - 1 ? '1px solid #E5E7EB' : 'none'
                    }}>
                      {staff.eintraege.map((eintrag, eintragIndex) => {
                        const tageImJahr = berechneArbeitstageImJahr(eintrag.von, eintrag.bis, jahr, eintrag.ist_halber_tag || false);
                        return (
                          <div
                            key={eintrag.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '10px 16px 10px 64px',
                              borderBottom: eintragIndex < staff.eintraege.length - 1 ? '1px solid #F3F4F6' : 'none',
                              fontSize: '13px'
                            }}
                          >
                            {/* Farb-Indikator */}
                            <div style={{
                              width: '3px',
                              height: '28px',
                              borderRadius: '2px',
                              backgroundColor: ABWESENHEITS_FARBEN[eintrag.art],
                              flexShrink: 0
                            }} />

                            {/* Zeitraum */}
                            <div style={{ flex: 1, color: '#374151' }}>
                              {eintrag.ist_halber_tag
                                ? `${formatieresDatum(eintrag.von)} · ½ Tag`
                                : `${formatieresDatum(eintrag.von)} — ${formatieresDatum(eintrag.bis)}`
                              }
                              <span style={{ color: '#9CA3AF', marginLeft: '8px' }}>
                                ({formatTage(tageImJahr)} {tageImJahr === 1 ? 'Arbeitstag' : 'Arbeitstage'})
                              </span>
                              {eintrag.notiz && (
                                <span style={{ color: '#9CA3AF', marginLeft: '8px' }}>
                                  · {eintrag.notiz}
                                </span>
                              )}
                            </div>

                            {/* Art Badge */}
                            <div style={{
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '500',
                              backgroundColor: eintrag.art === 'urlaub' ? '#EFF6FF' : '#FFFBEB',
                              color: eintrag.art === 'urlaub' ? '#2563EB' : '#D97706',
                              flexShrink: 0
                            }}>
                              {ABWESENHEITS_ARTEN[eintrag.art]}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {isAdmin && jahresLoading && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '20px',
          color: '#6B7280',
          marginTop: '24px'
        }}>
          <Loader size={16} style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }} />
          Jahresübersicht laden...
        </div>
      )}

      {/* Form Modal */}
      <UrlaubFormModal
        isOpen={showFormModal}
        onClose={() => {
          setShowFormModal(false);
          setBearbeitungsEintrag(null);
        }}
        onSave={handleSave}
        onDelete={handleDelete}
        eintrag={bearbeitungsEintrag}
        isAdmin={isAdmin}
        currentStaffId={currentStaffId}
      />
    </div>
  );
};
